import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiHelpCircle, FiChevronDown } from 'react-icons/fi';

const FAQSection = styled.section`
  padding: 6rem 2rem;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6496FF;
  margin-bottom: 1.5rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E8EAF0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled(motion.div)`
  background: rgba(25, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(20px);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(100, 150, 255, 0.3);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.5rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  color: #FFFFFF;
  font-size: 1.125rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(100, 150, 255, 0.05);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1.25rem;
  }
`;

const QuestionText = styled.span`
  flex: 1;
`;

const ChevronIcon = styled(motion.div)`
  color: #6496FF;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 1.5rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    padding: 0 1.25rem 1.25rem;
    font-size: 0.95rem;
  }
`;

const ContactCTA = styled(motion.div)`
  text-align: center;
  margin-top: 3rem;
  padding: 2rem;
  background: rgba(100, 150, 255, 0.05);
  border: 1px solid rgba(100, 150, 255, 0.2);
  border-radius: 16px;
  backdrop-filter: blur(10px);
`;

const CTATitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #FFFFFF;
  margin-bottom: 0.75rem;
`;

const CTAText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  color: #FFFFFF;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(100, 150, 255, 0.3);
  }
`;

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      question: "How does Meeting Copilot understand my meetings?",
      answer: "Meeting Copilot listens to your meetings in real-time and builds understanding of the context, participants, and discussion flow. It doesn't require any setup or explanation from you — it simply starts understanding from the moment it joins."
    },
    {
      question: "What happens when someone asks me a tough question?",
      answer: "Just ask your copilot! Since it's been listening to the entire conversation, it already knows the context and can provide you with instant, relevant answers without you having to explain the situation."
    },
    {
      question: "Is this just for certain types of meetings?",
      answer: "Meeting Copilot works for any type of meeting — whether you're pitching, troubleshooting, aligning, negotiating, or in customer calls. It's especially valuable for high-stakes situations where you need to maintain credibility."
    },
    {
      question: "How do I interact with my AI copilot during meetings?",
      answer: "You can quickly ask questions through a simple interface without disrupting the meeting flow. Your copilot responds instantly with context-aware answers based on what's been discussed."
    },
    {
      question: "Will other meeting participants know I'm using it?",
      answer: "Meeting Copilot operates silently and discretely. Other participants won't know you're using it unless you choose to tell them. It's designed to be your private AI teammate."
    },
    {
      question: "What platforms does it work with?",
      answer: "Meeting Copilot works with all major meeting platforms including Zoom, Teams, Google Meet, and more. It's available on macOS and Windows with no integration or setup required."
    },
    {
      question: "Is my meeting data secure?",
      answer: "Absolutely. Meeting Copilot processes information securely and maintains strict confidentiality. Your meeting content is protected and never shared or stored inappropriately."
    },
    {
      question: "How accurate are the responses?",
      answer: "Meeting Copilot provides highly accurate, context-aware responses because it's been actively listening and understanding your meeting. The AI is designed to help you maintain professional credibility in any situation."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <FAQSection id="faq" ref={ref}>
      <Container>
        <SectionHeader>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <FiHelpCircle />
            Frequently Asked Questions
          </Badge>
          
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Your Questions About Meeting Copilot
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Learn how your AI teammate can help you sound smart and stay confident in any meeting
          </Subtitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <FAQList>
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                variants={itemVariants}
                layout
              >
                <FAQQuestion onClick={() => toggleItem(index)}>
                  <QuestionText>{item.question}</QuestionText>
                  <ChevronIcon
                    animate={{ rotate: openItems.has(index) ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiChevronDown />
                  </ChevronIcon>
                </FAQQuestion>
                
                <AnimatePresence>
                  {openItems.has(index) && (
                    <FAQAnswer
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {item.answer}
                    </FAQAnswer>
                  )}
                </AnimatePresence>
              </FAQItem>
            ))}
          </FAQList>
        </motion.div>

        <ContactCTA
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <CTATitle>Ready to sound smart in every meeting?</CTATitle>
          <CTAText>
            Get your AI copilot and never be caught off guard again
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Meeting Copilot
          </CTAButton>
        </ContactCTA>
      </Container>
    </FAQSection>
  );
};

export default FAQ; 